/**
 * ReadyRow — kompakte Zeile im „Bereit"-Tab (T8): Attribut+Channel, gekürzte
 * Vorschau, Geprüft-Marker sobald einmal aufgeklappt. Aufgeklappt: Diff
 * (`DiffBlock` aus optimierungen/suggestionParts, T7-Wiederverwendung) +
 * Begründung (`ReasoningLine`) + Aktionen „Übernehmen"/„Bearbeiten"
 * (`SuggestionInlineEdit`)/„Verwerfen" (Trigger — Dialog rendert `ReadyBulkList`).
 * Lokales Edit-Staging analog `NeedsReviewCard.tsx` (Apply sendet den gestagten
 * Wert als `edited_value`).
 */
import { useState } from 'react'
import { ChevronRight, Check } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { DiffBlock } from '@/components/v2/products/optimierungen/suggestionParts'
import { ReasoningLine } from '@/components/v2/products/optimierungen/SuggestionCardExtras'
import { attrLabel } from '@/components/v2/products/optimierungen/labels'
import type { ChannelContentSuggestion } from '@/lib/api/channel-content-bulk'
import { EID } from '@/lib/element-ids.registry'

import { SuggestionInlineEdit } from './SuggestionInlineEdit'
import { ReadyRowActions } from './ReadyRowActions'

function previewText(value: string | string[]): string {
  const text = Array.isArray(value) ? value.join(' · ') : value
  return text.length > 90 ? `${text.slice(0, 90)}…` : text
}

export interface ReadyRowProps {
  suggestion: ChannelContentSuggestion
  channelName: string
  productLabel: string
  everSeen: boolean
  open: boolean
  busy: boolean
  onToggle: () => void
  onApply: (editedValue?: string | string[]) => void
  onDiscard: () => void
}

export function ReadyRow({ suggestion, channelName, productLabel, everSeen, open, busy, onToggle, onApply, onDiscard }: ReadyRowProps) {
  const { t } = useTranslation()
  const [editing, setEditing] = useState(false)
  const [staged, setStaged] = useState<string | string[] | null>(null)
  const effectiveValue = staged ?? suggestion.suggestion

  return (
    <article className="text-bulk-ready-row rounded-lg border border-neutral-200 dark:border-neutral-800">
      <button
        type="button"
        id={`${EID['text-bulk-ready-row-toggle-btn']}-${suggestion.id}`}
        onClick={onToggle}
        aria-expanded={open}
        className="flex w-full items-center gap-3 px-3 py-2.5 text-left hover:bg-neutral-50 dark:hover:bg-neutral-900"
      >
        <ChevronRight aria-hidden="true" className={`h-4 w-4 flex-none text-neutral-400 transition-transform ${open ? 'rotate-90' : ''}`} />
        <span className="w-32 flex-none text-xs text-neutral-500 dark:text-neutral-400">
          <span className="block truncate">
            {productLabel} · {channelName}
          </span>
          <span className="block text-sm-minus font-semibold text-neutral-800 dark:text-neutral-200">{attrLabel(suggestion.attribute_code)}</span>
        </span>
        <span className="flex-1 truncate text-sm text-neutral-600 dark:text-neutral-400">{previewText(effectiveValue)}</span>
        {everSeen && <Check aria-hidden="true" className="h-4 w-4 flex-none text-green-600 dark:text-green-400" />}
      </button>

      {open && (
        <div className="space-y-3 border-t border-neutral-200 px-3.5 py-3 dark:border-neutral-800">
          <Link
            to={`/v2/products/${suggestion.product_id}`}
            data-testid="ready-row-product-link"
            className="text-xs text-neutral-500 hover:text-primary-600 hover:underline dark:hover:text-primary-400"
          >
            {t('channelContent.review.openProduct')}
          </Link>
          <DiffBlock variant="alt" label={t('channelContent.card.old')} value={suggestion.previous_value} />
          {editing ? (
            <SuggestionInlineEdit
              idSuffix={suggestion.id}
              value={effectiveValue}
              onCancel={() => setEditing(false)}
              onSave={(value) => {
                setStaged(value)
                setEditing(false)
              }}
            />
          ) : (
            <DiffBlock variant="neu" label={t('channelContent.card.new')} value={effectiveValue} />
          )}
          {suggestion.reasoning && <ReasoningLine text={suggestion.reasoning} />}
          <ReadyRowActions
            idSuffix={suggestion.id}
            busy={busy}
            onEdit={() => setEditing(true)}
            onDiscard={onDiscard}
            onApply={() => onApply(staged ?? undefined)}
          />
        </div>
      )}
    </article>
  )
}
